import { ReactNode, useEffect, useState } from 'react';
import { makeStyles, Box } from '@material-ui/core';
import { Theme } from 'src/theme';
import NavBar from './NavBar';
import TopBar from './TopBar';
import HelpButton from 'src/components/Help/HelpButton';
import NotificationBanner from './TopBar/NotificationBanner';
import BillingBanner from './TopBar/BillingBanner';
import useAuth from 'src/hooks/useAuth';
import { getNotifications } from 'src/requests/notifications';
import VideoOverlay from './TopBar/VideoOverlay';
import { useBillingAlert } from 'src/hooks/useBillingAlert';

interface DashboardLayoutProps {
  children?: ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const classes = useStyles();
  const { user } = useAuth();
  const [isMobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const [notifications, setNotifications] = useState(null);
  const { data: billingAlert } = useBillingAlert(user?.organization?.organizationId);

  useEffect(() => {
    getNotifications(user.userId).then((res) => {
      if (res) {
        setNotifications(res);
      }
    });
  }, []);

  return (
    <Box className={classes.root}>
      <Box className={classes.noPrint}>
        <TopBar onMobileNavOpen={() => setMobileNavOpen(true)} />

        <Box className={classes.bannerContainer}>
          {billingAlert && <BillingBanner {...billingAlert} />}

          {notifications &&
            notifications.map((data) => {
              if (data.contentType === 'mp4') {
                return <VideoOverlay key={data.notificationId} data={data} id={user.userId} />;
              }

              return <NotificationBanner key={data.notificationId} data={data} id={user.userId} />;
            })}
        </Box>

        <NavBar onMobileClose={() => setMobileNavOpen(false)} openMobile={isMobileNavOpen} />
      </Box>

      <Box className={classes.wrapper}>
        <Box className={classes.contentContainer}>
          <Box className={classes.content} id="contentWrapper">
            <Box className={classes.noPrint}>
              <HelpButton />
            </Box>

            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    display: 'flex',
    height: '100%',
    width: '100%',
    '@media print': {
      backgroundColor: 'white',
    },
  },
  bannerContainer: {
    position: 'absolute',
    top: 64,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    zIndex: theme.zIndex.drawer + 100,
  },
  wrapper: {
    display: 'flex',
    flex: '1 1 auto',
    paddingTop: 64,
    [theme.breakpoints.up(1520)]: {
      paddingLeft: 256,
    },
  },
  contentContainer: {
    display: 'flex',
    flex: '1 1 auto',
  },
  content: {
    flex: '1 1 auto',
    height: '100%',
    overflow: 'auto',
    '@media print': {
      overflow: 'initial',
    },
  },
  noPrint: {
    '@media print': {
      display: 'none',
    },
  },
}));
